import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { tokens } from '../../theme/tokens';

interface SaveBarProps {
  /** The bar renders only while something is unsaved. */
  dirty: boolean;
  /** One line saying what saving affects. */
  message: React.ReactNode;
  saving?: boolean;
  /** Keeps Save off while the form is invalid. Discard stays available. */
  saveDisabled?: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

/**
 * Sticky save bar for a form that collects edits locally (ui-patterns.mdc, section 7):
 * a caution dot, a line on what saving affects, Discard and Save changes. Hidden
 * while nothing is unsaved.
 */
export const SaveBar: React.FC<SaveBarProps> = ({ dirty, message, saving = false, saveDisabled = false, onSave, onDiscard }) => {
  if (!dirty) return null;
  return (
    <Box
      role="region"
      aria-label="Unsaved changes"
      sx={{
        position: 'sticky',
        bottom: 16,
        zIndex: 2,
        mt: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        px: 2,
        py: 1.25,
        bgcolor: tokens.surface.card,
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(15,23,42,.12)',
        border: '1px solid',
        borderColor: tokens.line.default,
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.signal.caution.solid, flex: '0 0 auto' }} />
      <Typography sx={{ fontSize: 13.5, color: tokens.ink.body, flex: '1 1 220px' }}>{message}</Typography>
      <Button onClick={onDiscard} disabled={saving} sx={{ fontWeight: 600 }}>Discard</Button>
      <Button variant="contained" onClick={onSave} disabled={saving || saveDisabled} sx={{ fontWeight: 700 }}>
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </Box>
  );
};
